import React, { forwardRef } from 'react';
import {Tag as AntdTag} from 'antd';

/**
 * Antd's built-in preset Tag colours fail WCAG AA contrast in their default
 * light-background rendering. This map replaces each preset with a trio of
 * accessible values (background / border / text) that all meet ≥ 4.5 : 1.
 * Custom hex/rgb colours are passed through to antd unchanged.
 */
const ACCESSIBLE_COLORS = {
    pink:       {background: '#ffe4f0', borderColor: '#ffadd2', color: '#780650'},
    magenta:    {background: '#ffe4f0', borderColor: '#ffadd2', color: '#780650'},
    red:        {background: '#ffe4e4', borderColor: '#ffa39e', color: '#820014'},
    volcano:    {background: '#ffe7e0', borderColor: '#ffad9f', color: '#7c2206'},
    orange:     {background: '#fff3e0', borderColor: '#ffd591', color: '#7c4100'},
    gold:       {background: '#fff8e0', borderColor: '#ffe58f', color: '#614700'},
    yellow:     {background: '#fff8e0', borderColor: '#ffe58f', color: '#614700'},
    lime:       {background: '#f0ffe0', borderColor: '#d3f261', color: '#3a5c00'},
    green:      {background: '#e0fce0', borderColor: '#95de64', color: '#135200'},
    cyan:       {background: '#e0f8f8', borderColor: '#87e8de', color: '#00474f'},
    blue:       {background: '#e0eeff', borderColor: '#91caff', color: '#003998'},
    geekblue:   {background: '#e8eeff', borderColor: '#adb8ff', color: '#061178'},
    purple:     {background: '#f2e8ff', borderColor: '#d3adf7', color: '#391085'},
    // Semantic aliases
    success:    {background: '#e0fce0', borderColor: '#95de64', color: '#135200'},
    processing: {background: '#e0eeff', borderColor: '#91caff', color: '#003998'},
    error:      {background: '#ffe4e4', borderColor: '#ffa39e', color: '#820014'},
    warning:    {background: '#fff8e0', borderColor: '#ffe58f', color: '#614700'},
    default:    {background: '#f5f5f5', borderColor: '#d9d9d9', color: '#262626'},
};


/**
 * Accessible solid-variant colours. Each entry uses a background dark enough
 * to achieve ≥ 4.5 : 1 contrast against white text (WCAG AA). Values are
 * drawn from antd's palette but shifted one or two steps darker where the
 * default solid colour would fail (yellow, gold, orange, lime, cyan, blue).
 */
const SOLID_COLORS = {
    pink:       {background: '#c41d7f', color: '#ffffff'},
    magenta:    {background: '#c41d7f', color: '#ffffff'},
    red:        {background: '#cf1322', color: '#ffffff'},
    volcano:    {background: '#d4380d', color: '#ffffff'},
    orange:     {background: '#ad4e00', color: '#ffffff'},
    gold:       {background: '#874d00', color: '#ffffff'},
    yellow:     {background: '#876800', color: '#ffffff'},
    lime:       {background: '#3f6600', color: '#ffffff'},
    green:      {background: '#237804', color: '#ffffff'},
    cyan:       {background: '#006d75', color: '#ffffff'},
    blue:       {background: '#0958d9', color: '#ffffff'},
    geekblue:   {background: '#1d39c4', color: '#ffffff'},
    purple:     {background: '#531dab', color: '#ffffff'},
    // Semantic aliases
    success:    {background: '#237804', color: '#ffffff'},
    processing: {background: '#0958d9', color: '#ffffff'},
    error:      {background: '#cf1322', color: '#ffffff'},
    warning:    {background: '#874d00', color: '#ffffff'},
    default:    {background: '#595959', color: '#ffffff'},
};


const BUTTON_STYLE = {
    appearance: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    font: 'inherit',
    lineHeight: 'normal',
    display: 'inline-flex',
    alignItems: 'center',
};

/**
 * @typedef {import('antd').TagProps & {
 *   onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
 * }} TagProps
 * @remarks Providing `onClick` wraps the tag in a native `<button>` for accessibility.
 * Use only for label-like interactions (e.g. filter chips) — not as a substitute for `common/Button`.
 */

/** @type {React.FC<TagProps>} */
const Tag = forwardRef(({color, style, variant = 'filled', onClick, ...props}, ref) => {
    const preset = color && ACCESSIBLE_COLORS[color];
    const tagRef = onClick ? undefined : ref;

    let tag;
    if (preset) {
        if (variant === 'solid') {
            const solid = SOLID_COLORS[color] ?? {background: preset.color, color: '#ffffff'};
            tag = <AntdTag ref={tagRef} variant="solid" style={{...solid, ...style}} {...props} />;
        } else {
            const {borderColor, ...rest} = preset;
            const colorStyle = variant === 'outlined' ? {...rest, borderColor} : rest;
            tag = <AntdTag ref={tagRef} variant={variant} style={{...colorStyle, ...style}} {...props} />;
        }
    } else {
        tag = <AntdTag ref={tagRef} color={color} style={style} variant={variant} {...props} />;
    }

    if (!onClick) return tag;

    return (
        <button ref={ref} type="button" onClick={onClick} style={BUTTON_STYLE}>
            {tag}
        </button>
    );
});

export default Tag;
